import { getRecordingsForWidget } from '@hotjar/webapp/domains/recordings/api/recordings';
import type { Recording } from '@hotjar/webapp/domains/recordings/types';
import type { Segment } from '@hotjar/webapp/features/Recordings/types';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import * as Sentry from '@sentry/browser';
import type { InvalidateQueryFilters, UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';

const RECORDINGS_WIDGET_SESSION_KEY = 'recordings_widget';
const CACHE_TIME = 1000 * 60 * 60 * 24; // 24h

export const clearRecordingsWidgetData = (
  siteId: number,
  options: InvalidateQueryFilters = {},
): void => {
  try {
    Object.keys(sessionStorage)
      .filter((sessionKey) => {
        return sessionKey.includes(`${RECORDINGS_WIDGET_SESSION_KEY}_${siteId}`);
      })
      .forEach((sessionKey) => {
        sessionStorage.removeItem(sessionKey);
      });

    queryClient.invalidateQueries(`${RECORDINGS_WIDGET_SESSION_KEY}_${siteId}`, {
      refetchActive: false,
      refetchInactive: false,
      ...options,
    });
  } catch {
    Sentry.captureMessage('Error invalidating recordings widget cache', Sentry.Severity.Info);
  }
};

export const useRecordingsWidgetData = (
  siteId: number | undefined,
  segment: Segment | null,
  stateName: string,
): UseQueryResult<Recording[], Error> => {
  const cachedData = segment?.id
    ? window.sessionStorage.getItem(`${RECORDINGS_WIDGET_SESSION_KEY}_${siteId}_${segment?.id}`)
    : undefined;

  return useQuery(
    `${RECORDINGS_WIDGET_SESSION_KEY}_${siteId}_${segment?.id}`,
    () => getRecordingsForWidget(siteId as number, segment, stateName),
    {
      refetchOnWindowFocus: false,
      cacheTime: CACHE_TIME,
      staleTime: CACHE_TIME,
      onSuccess: (data) => {
        window.sessionStorage.setItem(
          `${RECORDINGS_WIDGET_SESSION_KEY}_${siteId}_${segment?.id}`,
          JSON.stringify(data),
        );
      },
      enabled: !!siteId && !!segment?.id,
      initialData: cachedData ? JSON.parse(cachedData) : undefined,
    },
  );
};
